/**
 * Arizona State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * Arizona REQUIRES state licensure for home health agencies under A.R.S. Title 36
 * 
 * Applicable Regulations:
 * - Home Health Agencies: A.A.C. R9-10-1201 through R9-10-1211 (Article 12)
 * - General Provisions: A.A.C. R9-10-101 through R9-10-113 (Article 1)
 * - TB Screening: A.A.C. R9-10-113
 * - Fingerprint Clearance Card: A.R.S. 36-411
 * - Mandated Reporter: A.R.S. 46-454
 * 
 * Key Article 12 Sections:
 * - R9-10-1201: Definitions
 * - R9-10-1202: Supplemental Application Requirements
 * - R9-10-1203: Administration
 * - R9-10-1204: Quality Management
 * - R9-10-1205: Contracted Services
 * - R9-10-1206: Personnel
 * - R9-10-1207: Care Plan
 * - R9-10-1208: Patient Rights
 * - R9-10-1209: Medical Records
 * - R9-10-1210: Home Health Services
 * - R9-10-1211: Supportive Services
 * 
 * License Renewal: Biennial (every 2 years)
 * Regulatory Body: Arizona Department of Health Services (ADHS)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const ARIZONA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'AZ-A1',
    itemId: 'A1',
    stateCode: 'AZ',
    title: 'ADHS Home Health Agency License Status',
    description: 'Arizona requires all home health agencies to be licensed by ADHS under A.A.C. R9-10 Article 12',
    severity: 'high',
    regulationCode: 'A.R.S. 36-405',
    regulationName: 'License',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov/licensing/special-licensing/home-health-agency/index.php',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from ADHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'HHA licenses must be renewed biennially (every 2 years). Check your license status at azdhs.gov',
      },
      {
        id: 'q2',
        question: 'What is your HHA license expiration date?',
        inputType: 'date',
        helpText: 'Found on your ADHS license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your ADHS Home Health Agency license is current and active per A.R.S. 36-405 and A.A.C. R9-10 Article 12.',
      atRisk: 'Your HHA license expires within 120 days. Arizona requires renewal applications to be submitted to ADHS before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home health agency in Arizona without a valid ADHS license violates A.R.S. 36-405 and can result in civil penalties up to $1,000 per day. Contact ADHS immediately at (602) 364-2536.',
      unknown: 'Verify your HHA license status at azdhs.gov or call ADHS at (602) 364-2536.',
    },
    stateNotes: [
      'HHA licenses are renewed every 2 years (biennial)',
      'Arizona follows Medicare Conditions of Participation',
      'ADHS conducts unannounced inspections',
      'CHAP accreditation may be accepted in lieu of state compliance inspection per A.R.S. 36-424',
      'All renewals must be submitted electronically via the Licensing Management System (LMS)',
    ],
  },

  {
    id: 'AZ-A2',
    itemId: 'A2',
    stateCode: 'AZ',
    title: 'Administrator Qualifications (R9-10-1203)',
    description: 'Arizona requires qualified administrators for home health agencies',
    severity: 'high',
    regulationCode: 'R9-10-1203',
    regulationName: 'Administrator',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a qualified administrator who meets ADHS requirements per R9-10-1203?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the administrator designated in writing with credentials on file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets R9-10-1203 requirements with documentation on file.',
      atRisk: 'Administrator documentation incomplete. Ensure written designation and credentials are on file.',
      nonCompliant: 'No qualified administrator designated per R9-10-1203.',
      unknown: 'Verify administrator qualifications per R9-10-1203.',
    },
    stateNotes: [
      'Administrator must be designated in writing per R9-10-1203',
      'Credentials must be documented and on file for ADHS review',
      'Administrator responsible for day-to-day operations and compliance',
      'ADHS surveys will review administrator qualifications and documentation',
    ],
  },

  {
    id: 'AZ-A3',
    itemId: 'A3',
    stateCode: 'AZ',
    title: 'Clinical Director/Director of Nursing (R9-10-1203)',
    description: 'Arizona requires a qualified clinical director or director of nursing',
    severity: 'high',
    regulationCode: 'R9-10-1203',
    regulationName: 'Clinical Director',
    agencyName: 'Arizona State Board of Nursing',
    agencyAbbr: 'AZBN',
    agencyPhone: '(602) 771-7800',
    agencyUrl: 'https://www.azbn.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a designated clinical director or director of nursing who is a licensed RN?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is their Arizona RN license current and verified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical director meets R9-10-1203 requirements with verified RN license.',
      atRisk: 'Verify RN license status with Arizona Board of Nursing.',
      nonCompliant: 'No qualified clinical director designated per R9-10-1203.',
      unknown: 'Review clinical director requirements per R9-10-1203.',
    },
    stateNotes: [
      'Clinical Director must be a licensed Registered Nurse in Arizona',
      'Verify license at azbn.gov/licenses-certifications',
      'RN license must be in good standing with no disciplinary actions',
      'Director of Nursing responsibilities defined in R9-10-1203',
    ],
  },

  {
    id: 'AZ-A4',
    itemId: 'A4',
    stateCode: 'AZ',
    title: 'Written Policies and Procedures (R9-10-1203)',
    description: 'Arizona requires specific written policies per R9-10-1203',
    severity: 'high',
    regulationCode: 'R9-10-1203',
    regulationName: 'Policies',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Arizona-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights (R9-10-1208)' },
          { value: 'care_plan', label: 'Care plan development and implementation (R9-10-1207)' },
          { value: 'services', label: 'Home health services scope (R9-10-1210)' },
          { value: 'supervision', label: 'Supervision of home health aides (R9-10-1210)' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Fingerprint clearance card procedures (A.R.S. 36-411)' },
          { value: 'personnel', label: 'Personnel qualifications and training (R9-10-1206)' },
          { value: 'infection', label: 'Infection control policies' },
          { value: 'abuse_reporting', label: 'Mandated reporter procedures (A.R.S. 46-454)' },
          { value: 'records', label: 'Medical records maintenance (R9-10-1209)' },
          { value: 'quality', label: 'Quality management (R9-10-1204)' },
          { value: 'emergency', label: 'Emergency and disaster preparedness' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Arizona R9-10-1203 requirements.',
      atRisk: 'Your policy manual is missing required sections per R9-10-1203. ADHS surveyors will review your policies for completeness.',
      nonCompliant: 'Multiple required policies are missing per R9-10-1203. This is a common ADHS survey deficiency.',
      unknown: 'Review your policies against R9-10-1203 requirements.',
    },
    stateNotes: [
      'Policies must be written, dated, and signed by administrator',
      'Review and update policies at least annually per R9-10-1203',
      'Policies must address all services provided by the agency',
      'Staff must be trained on policies and have access to policy manual',
      'ADHS surveys commonly cite incomplete policy manuals',
    ],
  },

  {
    id: 'AZ-A5',
    itemId: 'A5',
    stateCode: 'AZ',
    title: 'Quality Management Program (R9-10-1204)',
    description: 'Arizona requires a quality management program',
    severity: 'high',
    regulationCode: 'R9-10-1204',
    regulationName: 'Quality Management',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a formal quality management program per R9-10-1204?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which elements does your quality management program include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Written quality management policies' },
          { value: 'monitoring', label: 'Monitoring of patient care quality' },
          { value: 'incidents', label: 'Incident tracking and analysis' },
          { value: 'improvement', label: 'Performance improvement activities' },
          { value: 'documentation', label: 'Documentation of QM activities' },
          { value: 'review', label: 'Regular quality management reviews' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'How often do you conduct quality management reviews?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No regular reviews' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'includes', questionId: 'q3', value: ['monthly', 'quarterly'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'never', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Quality management program meets R9-10-1204 requirements.',
      atRisk: 'Quality management program needs enhancement or more frequent reviews.',
      nonCompliant: 'No quality management program exists. This violates R9-10-1204.',
      unknown: 'Review quality management requirements per R9-10-1204.',
    },
    stateNotes: [
      'QM program must monitor patient care quality per R9-10-1204',
      'Document QM activities quarterly at minimum',
      'Track and analyze incidents and adverse events',
      'Implement performance improvement plans based on findings',
      'ADHS expects documented evidence of QM activities during surveys',
    ],
  },

  {
    id: 'AZ-A6',
    itemId: 'A6',
    stateCode: 'AZ',
    title: 'Medicare/Medicaid Certification',
    description: 'Medicare and AHCCCS certification status',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoP',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as an AHCCCS (Arizona Medicaid) provider?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Medicare and AHCCCS certification active.',
      atRisk: 'Consider obtaining full Medicare/AHCCCS certification to expand patient base.',
      nonCompliant: 'Missing certifications limit patient access.',
      unknown: 'Verify certification status with CMS and AHCCCS.',
    },
    stateNotes: [
      'AHCCCS is Arizona\'s Medicaid program',
      'Medicare-certified agencies must meet both state and federal requirements',
      'AHCCCS ALTCS provides long-term care services',
    ],
  },

  {
    id: 'AZ-A7',
    itemId: 'A7',
    stateCode: 'AZ',
    title: 'Liability Insurance Coverage',
    description: 'Professional liability insurance requirements',
    severity: 'high',
    regulationCode: 'R9-10-1203',
    regulationName: 'Insurance',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain professional liability (malpractice) insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your liability coverage amount per occurrence?',
        inputType: 'select',
        options: [
          { value: '1m_plus', label: '$1,000,000 or more' },
          { value: '500k', label: '$500,000 - $999,999' },
          { value: 'under_500k', label: 'Under $500,000' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Do you maintain general liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: '1m_plus' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'under_500k', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Liability insurance coverage is adequate.',
      atRisk: 'Consider increasing liability coverage to at least $1,000,000.',
      nonCompliant: 'Operating without liability insurance creates significant risk.',
      unknown: 'Review insurance requirements.',
    },
    stateNotes: [
      'Professional liability insurance recommended minimum $1,000,000 per occurrence',
      'General liability insurance should cover premises and operations',
      'Maintain certificates of insurance on file for ADHS review',
      'Notify ADHS of any lapse in insurance coverage',
    ],
  },

  {
    id: 'AZ-A8',
    itemId: 'A8',
    stateCode: 'AZ',
    title: 'Workers Compensation Insurance',
    description: 'Arizona workers compensation coverage',
    severity: 'high',
    regulationCode: 'A.R.S. 23-901',
    regulationName: 'Workers Compensation',
    agencyName: 'Industrial Commission of Arizona',
    agencyAbbr: 'ICA',
    agencyPhone: '(602) 542-4661',
    agencyUrl: 'https://www.azica.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current workers compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Arizona requires workers compensation for employers with one or more employees.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation coverage meets Arizona requirements under A.R.S. 23-901.',
      atRisk: 'Verify workers compensation coverage status.',
      nonCompliant: 'Workers compensation required per A.R.S. 23-901. Failure to carry coverage can result in penalties.',
      unknown: 'Verify coverage with ICA at (602) 542-4661.',
    },
    stateNotes: [
      'Workers compensation required for all employers with 1+ employees in Arizona',
      'Coverage must be through authorized carrier or self-insurance per A.R.S. 23-961',
      'Failure to carry coverage is a Class 6 felony per A.R.S. 23-932',
      'ICA may audit employer compliance at any time',
    ],
  },

  {
    id: 'AZ-A9',
    itemId: 'A9',
    stateCode: 'AZ',
    title: 'Governing Body Structure (R9-10-1203)',
    description: 'Organizational governance requirements',
    severity: 'medium',
    regulationCode: 'R9-10-1203',
    regulationName: 'Governing Body',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated governing body per R9-10-1203?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is there a documented organizational chart?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governance structure documented per R9-10-1203.',
      atRisk: 'Document organizational structure with clear org chart.',
      nonCompliant: 'No governing body designated per R9-10-1203.',
      unknown: 'Review governance requirements per R9-10-1203.',
    },
    stateNotes: [
      'Governing body has ultimate responsibility for agency operations',
      'Document lines of authority and accountability in org chart',
      'Meeting minutes should reflect governance oversight activities',
      'ADHS surveys review governance documentation and authority structure',
    ],
  },

  {
    id: 'AZ-A10',
    itemId: 'A10',
    stateCode: 'AZ',
    title: 'Record Retention (R9-10-1209)',
    description: 'Arizona requires retention of medical records',
    severity: 'medium',
    regulationCode: 'R9-10-1209',
    regulationName: 'Medical Records',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records after discharge/termination?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer' },
          { value: '6', label: '6 years' },
          { value: '5', label: '5 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Arizona and Medicare require minimum 6-7 years retention for medical records.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '6', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '5', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Arizona requirements.',
      atRisk: 'Consider extending retention to at least 6-7 years to meet all requirements.',
      nonCompliant: 'Your record retention does not meet Arizona requirements. Update your policy immediately.',
      unknown: 'Review record retention requirements per R9-10-1209.',
    },
    stateNotes: [
      'Patient records must be retained minimum 6 years after discharge per R9-10-1209',
      'Medicare requires 7-year retention for certified agencies',
      'Records must be protected from unauthorized access, fire, and water damage',
      'Plan for record storage if agency closes or changes ownership',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'AZ-B1',
    itemId: 'B1',
    stateCode: 'AZ',
    title: 'Personnel File Requirements (R9-10-1206)',
    description: 'Arizona requires specific documents in each employee\'s personnel file',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'Personnel',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY direct care employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'fcc', label: 'Valid Fingerprint Clearance Card (Level One)' },
          { value: 'references', label: 'Reference checks' },
          { value: 'registry', label: 'Nurse Aide Registry verification (for HHAs)' },
          { value: 'oig', label: 'OIG/SAM exclusion list verification' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'training', label: 'Training documentation' },
          { value: 'competency', label: 'Competency evaluation' },
          { value: 'job_desc', label: 'Job description acknowledgment' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Arizona-required documentation per R9-10-1206.',
      atRisk: 'Some personnel files are missing required documents. ADHS surveyors will review personnel files.',
      nonCompliant: 'Personnel files are missing critical documentation required by R9-10-1206.',
      unknown: 'Audit personnel files against R9-10-1206 requirements.',
    },
    stateNotes: [
      'Personnel files must be complete before employee has patient contact',
      'ADHS randomly selects personnel files during surveys for review',
      'I-9 forms required within 3 days of hire per federal law',
      'Background check documentation is a common survey deficiency',
      'Keep original documents or certified copies on file',
    ],
  },

  {
    id: 'AZ-B2',
    itemId: 'B2',
    stateCode: 'AZ',
    title: 'Professional License Verification (R9-10-1206)',
    description: 'Verification of all professional licenses',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'License Verification',
    agencyName: 'Arizona State Board of Nursing',
    agencyAbbr: 'AZBN',
    agencyPhone: '(602) 771-7800',
    agencyUrl: 'https://www.azbn.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, etc.) before hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with licensing board' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and verify renewal?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking system with alerts' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'License verification process meets R9-10-1206 requirements.',
      atRisk: 'License verification is inconsistent. Implement systematic verification.',
      nonCompliant: 'Employing unlicensed professionals violates Arizona law.',
      unknown: 'Review license verification procedures.',
    },
    stateNotes: [
      'Verify licenses at azbn.gov before first patient assignment',
      'Check for disciplinary actions or restrictions on license',
      'RN licenses in Arizona expire on birthday in odd/even years',
      'Compact license holders must verify Arizona recognition',
      'Document verification date and source in personnel file',
    ],
  },

  {
    id: 'AZ-B3',
    itemId: 'B3',
    stateCode: 'AZ',
    title: 'Fingerprint Clearance Card (A.R.S. 36-411)',
    description: 'All direct care staff must have valid Fingerprint Clearance Card',
    severity: 'high',
    regulationCode: 'A.R.S. 36-411',
    regulationName: 'Fingerprint Clearance',
    agencyName: 'Arizona Department of Public Safety',
    agencyAbbr: 'AZ DPS',
    agencyPhone: '(602) 223-2279',
    agencyUrl: 'https://www.azdps.gov/services/public-services-center/fingerprint-clearance-card',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care staff have a valid Arizona Fingerprint Clearance Card (Level One) on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have valid Level One FCCs' },
          { value: 'most', label: 'Most have them, some are pending or expired' },
          { value: 'no', label: 'No / not sure about FCC status' },
        ],
        required: true,
        helpText: 'Level One FCCs are required for healthcare workers. FCCs must be renewed every 6 years.',
      },
      {
        id: 'q2',
        question: 'Do staff begin patient care BEFORE their FCC is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before FCC clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before FCC clearance (direct violation of A.R.S. 36-411)
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if no FCCs on file
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT only if all have FCCs AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if some FCCs pending/expired
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'All staff have valid Fingerprint Clearance Cards per A.R.S. 36-411.',
      atRisk: 'Some FCCs pending/expired. Ensure all staff have valid FCCs before patient contact.',
      nonCompliant: 'VIOLATION: Staff are working before FCC clearance OR working without valid FCCs. Per A.R.S. 36-411, no patient contact is permitted until FCC is received.',
      unknown: 'Verify all staff have valid Level One FCCs on file.',
    },
    stateNotes: [
      'Level One FCC required for all healthcare workers',
      'FCCs are valid for 6 years',
      'Apply through AZ DPS Fingerprint Clearance Card Program',
      'Certain criminal convictions are absolute bars to clearance',
    ],
  },

  {
    id: 'AZ-B4',
    itemId: 'B4',
    stateCode: 'AZ',
    title: 'FCC Renewal Tracking',
    description: 'Tracking Fingerprint Clearance Card renewals',
    severity: 'medium',
    regulationCode: 'A.R.S. 36-411',
    regulationName: 'FCC Renewal',
    agencyName: 'Arizona Department of Public Safety',
    agencyAbbr: 'AZ DPS',
    agencyPhone: '(602) 223-2279',
    agencyUrl: 'https://www.azdps.gov/services/public-services-center/fingerprint-clearance-card',
    questions: [
      {
        id: 'q1',
        question: 'Do you track FCC expiration dates (6-year renewal)?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking with alerts' },
          { value: 'manual', label: 'Manual tracking' },
          { value: 'none', label: 'No tracking' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['system', 'manual'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'FCC renewal tracking in place.',
      atRisk: 'Review tracking system effectiveness.',
      nonCompliant: 'No FCC expiration tracking. Staff may work with expired FCCs.',
      unknown: 'Implement FCC tracking system.',
    },
    stateNotes: [
      'FCCs are valid for 6 years from issue date',
      'Renewal application should be submitted 60-90 days before expiration',
      'Expired FCC = immediate removal from patient care until renewed',
      'Track renewal deadlines in automated system with advance alerts',
    ],
  },

  {
    id: 'AZ-B5',
    itemId: 'B5',
    stateCode: 'AZ',
    title: 'Job Descriptions (R9-10-1206)',
    description: 'Written job descriptions for all positions',
    severity: 'medium',
    regulationCode: 'R9-10-1206',
    regulationName: 'Personnel',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does every employee have a written job description?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do employees sign acknowledgment of their job description?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Job descriptions complete and signed.',
      atRisk: 'Obtain signed acknowledgments for all job descriptions.',
      nonCompliant: 'Job descriptions missing for some positions.',
      unknown: 'Review job description requirements per R9-10-1206.',
    },
    stateNotes: [
      'Job descriptions must outline duties, responsibilities, and scope of practice',
      'Employee signature acknowledges understanding of role',
      'Update job descriptions when duties change significantly',
      'Include reporting relationships and supervision requirements',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'AZ-C1',
    itemId: 'C1',
    stateCode: 'AZ',
    title: 'Background Check Requirements (A.R.S. 36-411)',
    description: 'Arizona requires comprehensive background screening for all direct care staff',
    severity: 'high',
    regulationCode: 'A.R.S. 36-411',
    regulationName: 'Background Check',
    agencyName: 'Arizona Department of Public Safety',
    agencyAbbr: 'AZ DPS',
    agencyPhone: '(602) 223-2279',
    agencyUrl: 'https://www.azdps.gov/services/public-services-center/fingerprint-clearance-card',
    questions: [
      {
        id: 'q1',
        question: 'For ALL direct care staff, which background checks do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'fcc', label: 'Valid Arizona Fingerprint Clearance Card (Level One)' },
          { value: 'registry', label: 'Arizona Nurse Aide Registry verification' },
          { value: 'sex_offender', label: 'Sex Offender Registry check' },
          { value: 'adult_ps', label: 'Adult Protective Services Registry check' },
          { value: 'oig', label: 'OIG/SAM exclusion list verification' },
        ],
        required: true,
        helpText: 'Arizona requires Level One FCC before patient contact per A.R.S. 36-411',
      },
      {
        id: 'q2',
        question: 'Are ALL background checks completed BEFORE any patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before patient contact' },
          { value: 'no', label: 'No - sometimes staff start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation of A.R.S. 36-411)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND always complete before patient contact
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 2-3 checks but not all required
      { condition: 'countBetween', questionId: 'q1', value: [2, 3], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete for all direct care staff per A.R.S. 36-411.',
      atRisk: 'Background checks may be incomplete. Review all staff files and ensure all required checks are completed.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per A.R.S. 36-411, no patient contact is permitted until all checks are completed.',
      unknown: 'Audit all staff files for background check documentation.',
    },
    stateNotes: [
      'All background checks must be completed BEFORE any patient contact',
      'Level One FCC is mandatory for all direct care healthcare workers',
      'OIG/SAM checks should be monthly for Medicare/Medicaid agencies',
      'Document date and results of each check type in personnel file',
    ],
  },

  {
    id: 'AZ-C2',
    itemId: 'C2',
    stateCode: 'AZ',
    title: 'Adult Protective Services Registry Check (A.R.S. 46-459)',
    description: 'Arizona requires APS registry verification for all home health employees per A.R.S. 36-411 and 46-459',
    severity: 'high',
    regulationCode: 'A.R.S. 46-459',
    regulationName: 'APS Registry',
    agencyName: 'Arizona Department of Economic Security - Adult Protective Services',
    agencyAbbr: 'AZ DES/APS',
    agencyPhone: '(877) 767-2385',
    agencyUrl: 'https://des.az.gov/services/basic-needs/adult-protective-services/employers',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the APS Central Registry for all employees before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'As of January 1, 2025, A.R.S. 36-411 requires employers to verify employees are not on the APS registry before hire.',
      },
      {
        id: 'q2',
        question: 'Have you verified ALL current employees against the APS registry?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - all current staff verified' },
          { value: 'no', label: 'No / Not yet' },
        ],
        required: true,
        helpText: 'By March 31, 2025, employers must verify all existing employees are not on the APS registry.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'APS registry checks completed for all employees per A.R.S. 46-459.',
      atRisk: 'APS registry checks may be incomplete. Verify all new hires and existing employees against the registry.',
      nonCompliant: 'APS registry checks not being conducted. This violates A.R.S. 36-411. Employers cannot hire individuals on the registry.',
      unknown: 'Review APS registry check requirements at des.az.gov.',
    },
    stateNotes: [
      'Effective January 1, 2025: Must verify new hires are not on APS registry before employment',
      'By March 31, 2025: Must verify ALL existing employees are not on the APS registry',
      'If employee is on registry, employment must be terminated',
      'Check registry at des.az.gov/services/basic-needs/adult-protective-services/employers',
    ],
  },

  {
    id: 'AZ-C3',
    itemId: 'C3',
    stateCode: 'AZ',
    title: 'Nurse Aide Registry Verification',
    description: 'Verification of home health aides on Arizona Nurse Aide Registry',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'Aide Registry',
    agencyName: 'Arizona State Board of Nursing',
    agencyAbbr: 'AZBN',
    agencyPhone: '(602) 771-7800',
    agencyUrl: 'https://www.azbn.gov/licenses-certifications/nurse-aide-registry',
    questions: [
      {
        id: 'q1',
        question: 'Are all home health aides verified on the Arizona Nurse Aide Registry?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, 100% are verified' },
          { value: 'most', label: 'Most are verified, some pending' },
          { value: 'some', label: 'Some are not verified' },
          { value: 'unsure', label: 'Not sure how to check' },
        ],
        required: true,
        helpText: 'Check registry status at azbn.gov',
      },
      {
        id: 'q2',
        question: 'Do you verify registry status BEFORE assigning aides to patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always verify first' },
          { value: 'no', label: 'No / not consistently' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['some', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides verified on Arizona Nurse Aide Registry.',
      atRisk: 'Some aides pending verification or checks are inconsistent.',
      nonCompliant: 'Using unverified aides violates Arizona requirements.',
      unknown: 'Check all aide registry status at azbn.gov.',
    },
    stateNotes: [
      'Verify at azbn.gov/licenses-certifications/nurse-aide-registry',
      'Check for findings of abuse, neglect, or misappropriation',
      'Registry status must be verified before patient assignment',
      'Document verification date and registry status in personnel file',
    ],
  },

  {
    id: 'AZ-C4',
    itemId: 'C4',
    stateCode: 'AZ',
    title: 'OIG Exclusion List Check',
    description: 'Federal exclusion list verification for Medicare/Medicaid billing',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list (LEIE) before hiring?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you re-check for current employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'Only at hire' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'monthly' },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion checks completed at hire and monthly.',
      atRisk: 'OIG recommends monthly screening. Consider increasing frequency.',
      nonCompliant: 'OIG checks not conducted. Billing for excluded individuals is Medicare fraud.',
      unknown: 'Review OIG exclusion check requirements.',
    },
    stateNotes: [
      'Check LEIE at exclusions.oig.hhs.gov monthly',
      'Also check SAM.gov for federal debarment/exclusion',
      'Billing for excluded individuals is federal fraud with severe penalties',
      'Maintain documentation of each monthly check with results',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'AZ-D1',
    itemId: 'D1',
    stateCode: 'AZ',
    title: 'TB Screening (R9-10-113)',
    description: 'Arizona requires TB screening for all direct care staff',
    severity: 'high',
    regulationCode: 'R9-10-113',
    regulationName: 'TB Screening',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff have current TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be expired' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed within 12 months before providing services per R9-10-113.',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening expiration dates?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff per R9-10-113.',
      atRisk: 'Some TB screenings may be expired or tracking is inadequate.',
      nonCompliant: 'Staff working without valid TB screening. This violates R9-10-113.',
      unknown: 'Verify TB screening status for all staff per R9-10-113.',
    },
    stateNotes: [
      'TB screening required within 12 months before providing services',
      'Acceptable tests: Mantoux skin test or CDC-recommended blood test',
      'Positive tests require follow-up per R9-10-113(B)',
    ],
  },

  {
    id: 'AZ-D2',
    itemId: 'D2',
    stateCode: 'AZ',
    title: 'Emergency Preparedness Plan',
    description: 'Arizona emergency preparedness requirements',
    severity: 'high',
    regulationCode: 'R9-10-1203',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written emergency preparedness plan?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your plan address Arizona-specific hazards?',
        inputType: 'checklist',
        options: [
          { value: 'heat', label: 'Extreme heat emergencies' },
          { value: 'dust', label: 'Dust storms (haboobs)' },
          { value: 'flood', label: 'Flash flooding (monsoon season)' },
          { value: 'fire', label: 'Wildfire and smoke' },
          { value: 'power', label: 'Power outages' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'When was your emergency plan last reviewed and tested?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: '1_2_years', label: '1-2 years ago' },
          { value: 'over_2', label: 'Over 2 years ago' },
          { value: 'never', label: 'Never tested' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
        { condition: 'equals', questionId: 'q3', value: 'within_year' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Emergency plan addresses Arizona-specific hazards and is current.',
      atRisk: 'Update emergency plan to address Arizona-specific hazards.',
      nonCompliant: 'No emergency preparedness plan documented.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Arizona experiences extreme heat (110°F+) in summer',
      'Monsoon season (June-September) brings flash flooding and dust storms',
      'Wildfire season can affect air quality statewide',
    ],
  },

  {
    id: 'AZ-D3',
    itemId: 'D3',
    stateCode: 'AZ',
    title: 'Infection Control Program',
    description: 'Infection control policies and procedures',
    severity: 'high',
    regulationCode: 'R9-10-1203',
    regulationName: 'Infection Control',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection control program that includes:',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'ppe', label: 'Personal protective equipment (PPE) guidelines' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure procedures' },
          { value: 'outbreak', label: 'Disease outbreak response plan' },
          { value: 'training', label: 'Annual infection control training' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program is comprehensive.',
      atRisk: 'Infection control program is incomplete. Update policies.',
      nonCompliant: 'Infection control program does not meet requirements.',
      unknown: 'Review infection control program requirements.',
    },
    stateNotes: [
      'Infection control policies must follow CDC guidelines',
      'Annual training on infection control is required',
      'Document all disease outbreaks and actions taken',
      'Bloodborne pathogen exposure plan required per OSHA',
    ],
  },

  {
    id: 'AZ-D4',
    itemId: 'D4',
    stateCode: 'AZ',
    title: 'PPE and Safety Equipment',
    description: 'Personal protective equipment availability',
    severity: 'high',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'OSHA Safety',
    agencyName: 'Arizona Division of Occupational Safety and Health',
    agencyAbbr: 'ADOSH',
    agencyPhone: '(602) 542-5795',
    agencyUrl: 'https://www.azica.gov/divisions/adosh',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain adequate PPE supplies for all staff?',
        inputType: 'checklist',
        options: [
          { value: 'gloves', label: 'Disposable gloves' },
          { value: 'masks', label: 'Face masks/N95 respirators' },
          { value: 'gowns', label: 'Isolation gowns' },
          { value: 'eyewear', label: 'Eye protection' },
          { value: 'sanitizer', label: 'Hand sanitizer' },
          { value: 'bags', label: 'Biohazard bags' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are staff trained on proper PPE use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PPE supplies and training are adequate.',
      atRisk: 'Some PPE supplies or training may be missing.',
      nonCompliant: 'Inadequate PPE supplies or no training.',
      unknown: 'Review PPE requirements.',
    },
    stateNotes: [
      'PPE must be provided at no cost to employees per OSHA 29 CFR 1910.1030',
      'Annual training on proper PPE donning and doffing required',
      'Maintain adequate supply levels for all client visits',
      'Document PPE training in personnel files',
    ],
  },

  {
    id: 'AZ-D5',
    itemId: 'D5',
    stateCode: 'AZ',
    title: 'Heat Safety Protocols',
    description: 'Arizona-specific extreme heat safety for staff and patients',
    severity: 'medium',
    regulationCode: 'R9-10-1203',
    regulationName: 'Heat Safety',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have heat safety protocols for staff and patients?',
        inputType: 'checklist',
        options: [
          { value: 'hydration', label: 'Hydration requirements for staff' },
          { value: 'scheduling', label: 'Visit scheduling during cooler hours' },
          { value: 'vehicle', label: 'Vehicle safety (never leave patients/supplies in hot cars)' },
          { value: 'patient_assessment', label: 'Heat-related illness assessment for patients' },
          { value: 'cancellation', label: 'Excessive heat warning protocols' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Heat safety protocols are comprehensive for Arizona conditions.',
      atRisk: 'Enhance heat safety procedures.',
      nonCompliant: 'Heat safety protocols are insufficient for Arizona conditions.',
      unknown: 'Review heat safety requirements.',
    },
    stateNotes: [
      'Phoenix averages 110+ days over 100°F annually',
      'Heat is the #1 weather-related cause of death in Arizona',
      'Extreme heat warnings should trigger modified visit schedules',
    ],
  },

  {
    id: 'AZ-D6',
    itemId: 'D6',
    stateCode: 'AZ',
    title: 'Incident Reporting System',
    description: 'System for reporting safety incidents',
    severity: 'high',
    regulationCode: 'R9-10-1204',
    regulationName: 'Incident Reporting',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an incident reporting system?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are incidents reviewed and analyzed for improvement?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting system in place with analysis for improvement.',
      atRisk: 'Incidents should be analyzed for quality improvement.',
      nonCompliant: 'No incident reporting system.',
      unknown: 'Review incident reporting requirements.',
    },
    stateNotes: [
      'Report incidents to ADHS as required per R9-10-1204',
      'Serious incidents may require immediate reporting to ADHS',
      'Analyze trends quarterly as part of QM program',
      'Document corrective actions and follow-up for each incident',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'AZ-E1',
    itemId: 'E1',
    stateCode: 'AZ',
    title: 'Home Health Aide Training (R9-10-1206)',
    description: 'Arizona requires training for home health aides',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'Training',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new home health aides complete the following BEFORE any patient contact?',
        inputType: 'checklist',
        options: [
          { value: '75_hours', label: '75 hours of training (federal minimum for Medicare)' },
          { value: 'classroom', label: 'Classroom instruction' },
          { value: 'clinical', label: '16 hours clinical/practical experience' },
          { value: 'competency', label: 'Competency evaluation by RN' },
          { value: 'documentation', label: 'Training documentation in personnel file' },
        ],
        required: true,
        helpText: 'Medicare-certified agencies must meet federal 75-hour training requirement.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training program meets requirements.',
      atRisk: 'HHA training may be incomplete. Review training components.',
      nonCompliant: 'HHAs providing services without required training.',
      unknown: 'Review HHA training requirements per R9-10-1206.',
    },
    stateNotes: [
      'Federal minimum 75 hours for Medicare-certified agencies per 42 CFR 484.36',
      'Must include 16 hours of supervised clinical experience',
      'Competency evaluation by RN required before patient care',
      'Training must cover all areas specified in R9-10-1206',
    ],
  },

  {
    id: 'AZ-E2',
    itemId: 'E2',
    stateCode: 'AZ',
    title: 'Competency Evaluation (R9-10-1206)',
    description: 'Skills competency assessment for all aides',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'Competency',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct competency evaluations for all home health aides?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, for all aides' },
          { value: 'some', label: 'For some aides' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the competency evaluation conducted by an RN?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'How often are competencies re-evaluated?',
        inputType: 'select',
        options: [
          { value: 'annually', label: 'Annually' },
          { value: 'biannually', label: 'Every 2 years' },
          { value: 'hire_only', label: 'Only at hire' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'annually' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluations meet requirements.',
      atRisk: 'Expand competency program to all aides.',
      nonCompliant: 'No competency evaluations being conducted.',
      unknown: 'Review competency evaluation requirements.',
    },
    stateNotes: [
      'Competency evaluation must be conducted by qualified RN',
      'Evaluate skills in all areas HHA will perform per R9-10-1206',
      'Annual competency re-evaluation recommended',
      'Document evaluation results and any retraining needs',
    ],
  },

  {
    id: 'AZ-E3',
    itemId: 'E3',
    stateCode: 'AZ',
    title: 'Annual In-Service Training (R9-10-1206)',
    description: 'Ongoing training requirements for home health aides',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'Annual Training',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides complete 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'safety', label: 'Safety and infection control' },
          { value: 'patient_rights', label: 'Patient rights' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'abuse', label: 'Abuse/neglect recognition and reporting' },
          { value: 'heat', label: 'Heat safety (Arizona-specific)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets requirements (12 hours with required topics).',
      atRisk: 'Some aides may be behind on annual training.',
      nonCompliant: 'No structured annual training program exists.',
      unknown: 'Review annual training requirements.',
    },
    stateNotes: [
      '12 hours annual in-service training required for Medicare-certified agencies',
      'Training must relate to job duties and patient care needs',
      'Include Arizona-specific topics like heat safety',
      'Track training hours and topics in personnel files',
    ],
  },

  {
    id: 'AZ-E4',
    itemId: 'E4',
    stateCode: 'AZ',
    title: 'Mandated Reporter Training (A.R.S. 46-454)',
    description: 'All staff are mandated reporters of abuse and neglect',
    severity: 'high',
    regulationCode: 'A.R.S. 46-454',
    regulationName: 'Mandated Reporting',
    agencyName: 'Arizona Department of Economic Security',
    agencyAbbr: 'AZ DES',
    agencyPhone: '(877) 767-2385',
    agencyUrl: 'https://des.az.gov/services/aging-and-adult/adult-protective-services',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care staff receive training on mandated reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you train staff on how to identify and report suspected abuse, neglect, or exploitation?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
          { value: 'orientation_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'orientation_only' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on mandated reporter duties per A.R.S. 46-454.',
      atRisk: 'Some staff may lack mandated reporter training. Complete training for all staff.',
      nonCompliant: 'Failure to train staff on mandated reporter duties violates A.R.S. 46-454.',
      unknown: 'Verify all staff have received mandated reporter training.',
    },
    stateNotes: [
      'Arizona APS Hotline: (877) 767-2385',
      'Reports must be made immediately upon reasonable belief',
      'Failure to report is a Class 1 misdemeanor',
    ],
  },

  {
    id: 'AZ-E5',
    itemId: 'E5',
    stateCode: 'AZ',
    title: 'CPR and First Aid Certification',
    description: 'Emergency response training for direct care staff',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'Emergency Training',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do all direct care staff maintain current CPR certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current CPR' },
          { value: 'most', label: 'Most staff, some may be expired' },
          { value: 'no', label: 'No / Not required' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are certifications from approved providers (AHA, Red Cross)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CPR certifications are current for all direct care staff.',
      atRisk: 'Some CPR certifications may be expired. Schedule recertification.',
      nonCompliant: 'Staff lacking CPR certification.',
      unknown: 'Review CPR certification requirements.',
    },
    stateNotes: [
      'CPR certification typically valid for 2 years',
      'AHA or Red Cross certifications are widely accepted',
      'Track expiration dates with 30-day advance reminders',
      'Online certifications may not meet agency requirements',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'AZ-F1',
    itemId: 'F1',
    stateCode: 'AZ',
    title: 'Care Plan Requirements (R9-10-1207)',
    description: 'Arizona requires individualized care plans for each patient',
    severity: 'high',
    regulationCode: 'R9-10-1207',
    regulationName: 'Care Plan',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written care plan for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your care plans include all required elements per R9-10-1207?',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Diagnosis and prognosis' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Measurable goals and expected outcomes' },
          { value: 'physician', label: 'Physician orders/certification' },
          { value: 'medications', label: 'Medications and treatments' },
        ],
      },
      {
        id: 'q3',
        question: 'Are care plans reviewed and updated as patient condition changes?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Care plans meet R9-10-1207 requirements.',
      atRisk: 'Care plans may be incomplete or created after services begin.',
      nonCompliant: 'Services being provided without written care plans. This violates R9-10-1207.',
      unknown: 'Review care plan process against R9-10-1207 requirements.',
    },
    stateNotes: [
      'Care plan must be completed before first patient visit',
      'Physician orders/certification required per R9-10-1207',
      'Update care plan when patient condition changes',
      'Care plan must be reviewed at least every 60 days',
    ],
  },

  {
    id: 'AZ-F2',
    itemId: 'F2',
    stateCode: 'AZ',
    title: 'Supervision of Home Health Aides (R9-10-1210)',
    description: 'Arizona requires RN supervision of home health aides every 2 weeks',
    severity: 'high',
    regulationCode: 'R9-10-1210',
    regulationName: 'Services',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'How often does an RN supervise each home health aide in the patient\'s home?',
        inputType: 'select',
        options: [
          { value: 'every_2_weeks', label: 'Every 2 weeks or more frequently' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'rarely', label: 'Less frequently / as needed' },
          { value: 'never', label: 'No formal supervision schedule' },
        ],
        required: true,
        helpText: 'R9-10-1210 requires RN to meet with patient receiving HHA services every two weeks.',
      },
      {
        id: 'q2',
        question: 'Is supervision documented in patient records?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always documented' },
          { value: 'no', label: 'No / inconsistently' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no schedule, rare supervision, OR not documented
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['rarely', 'never'] },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT: every 2 weeks and documented
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_2_weeks' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: every 30 days (slightly off but exists)
      { condition: 'equals', questionId: 'q1', value: 'every_30', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets Arizona R9-10-1210 requirements (every 2 weeks with documentation).',
      atRisk: 'Supervision frequency or documentation needs improvement.',
      nonCompliant: 'HHAs are not receiving required RN supervision per R9-10-1210.',
      unknown: 'Review supervision practices against R9-10-1210 requirements.',
    },
    stateNotes: [
      'RN must meet with patient receiving HHA services every 2 weeks per R9-10-1210',
      'Supervision must be documented in patient record',
      'RN should observe HHA providing care at least once per supervisory cycle',
      'Address any competency issues identified during supervision',
    ],
  },

  {
    id: 'AZ-F3',
    itemId: 'F3',
    stateCode: 'AZ',
    title: 'Patient Rights (R9-10-1208)',
    description: 'Patient rights notification and compliance',
    severity: 'high',
    regulationCode: 'R9-10-1208',
    regulationName: 'Patient Rights',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are patients informed of their rights at admission per R9-10-1208?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients receive written notice including:',
        inputType: 'checklist',
        options: [
          { value: 'rights', label: 'Statement of patient rights' },
          { value: 'services', label: 'Services to be provided' },
          { value: 'charges', label: 'Charges and payment policies' },
          { value: 'complaint', label: 'Complaint procedures' },
          { value: 'hotline', label: 'ADHS complaint hotline information' },
        ],
      },
      {
        id: 'q3',
        question: 'Do patients sign acknowledgment of receiving rights information?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets R9-10-1208 requirements.',
      atRisk: 'Obtain signed acknowledgments and ensure all required information is provided.',
      nonCompliant: 'Patient rights not communicated per R9-10-1208.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      'Provide written patient rights at admission per R9-10-1208',
      'Include ADHS complaint hotline: (602) 364-2536',
      'Obtain signed acknowledgment in patient preferred language',
      'Rights must be posted in visible location at agency',
    ],
  },

  {
    id: 'AZ-F4',
    itemId: 'F4',
    stateCode: 'AZ',
    title: 'Care Coordination (R9-10-1210)',
    description: 'Coordination with physicians and other providers',
    severity: 'medium',
    regulationCode: 'R9-10-1210',
    regulationName: 'Care Coordination',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you coordinate care with patients\' primary physicians?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is coordination documented in the patient record?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you immediately inform the physician of changes in patient condition?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Care coordination documented per R9-10-1210.',
      atRisk: 'Document coordination activities and physician communications.',
      nonCompliant: 'No care coordination with physicians.',
      unknown: 'Review care coordination requirements per R9-10-1210.',
    },
    stateNotes: [
      'Document all physician communications in patient record',
      'Notify physician immediately of condition changes per R9-10-1210',
      'Coordinate with other providers for continuity of care',
      'Obtain updated orders as care needs change',
    ],
  },

  {
    id: 'AZ-F5',
    itemId: 'F5',
    stateCode: 'AZ',
    title: 'Advance Directive Documentation (A.R.S. 36-3221)',
    description: 'Documentation of patient advance directives',
    severity: 'medium',
    regulationCode: 'A.R.S. 36-3221',
    regulationName: 'Advance Directives',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you document whether patients have advance directives?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide information about advance directives to patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Advance directive procedures meet A.R.S. 36-3221 requirements.',
      atRisk: 'Provide advance directive information to patients.',
      nonCompliant: 'Not documenting advance directives per A.R.S. 36-3221.',
      unknown: 'Review advance directive requirements.',
    },
    stateNotes: [
      'Inquire about advance directives at admission per A.R.S. 36-3221',
      'Document presence or absence of advance directives in record',
      'Provide information on creating advance directives if none exist',
      'Follow advance directives as applicable during care',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'AZ-G1',
    itemId: 'G1',
    stateCode: 'AZ',
    title: 'Medical Records Requirements (R9-10-1209)',
    description: 'Patient medical record documentation requirements',
    severity: 'high',
    regulationCode: 'R9-10-1209',
    regulationName: 'Medical Records',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do patient medical records include all required elements per R9-10-1209?',
        inputType: 'checklist',
        options: [
          { value: 'patient_info', label: 'Patient identification and demographics' },
          { value: 'physician_orders', label: 'Physician/practitioner orders' },
          { value: 'care_plan', label: 'Care plan' },
          { value: 'visit_notes', label: 'Documentation of each visit/service' },
          { value: 'assessments', label: 'Clinical assessments' },
          { value: 'medications', label: 'Medication list' },
          { value: 'progress', label: 'Progress notes' },
          { value: 'discharge', label: 'Discharge summary' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are medical records completed timely after each visit?',
        inputType: 'select',
        options: [
          { value: 'same_day', label: 'Yes, same day or within 24 hours' },
          { value: 'within_week', label: 'Within a week' },
          { value: 'delayed', label: 'Often delayed beyond a week' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'same_day' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medical records contain all required elements and are completed timely.',
      atRisk: 'Medical records may be missing elements or documentation is delayed.',
      nonCompliant: 'Medical records do not meet R9-10-1209 requirements.',
      unknown: 'Review medical record requirements per R9-10-1209.',
    },
    stateNotes: [
      'Complete visit documentation same day or within 24 hours',
      'All entries must be dated, timed, and signed per R9-10-1209',
      'Late entries must be clearly identified as such',
      'ADHS surveys review random sample of patient records',
    ],
  },

  {
    id: 'AZ-G2',
    itemId: 'G2',
    stateCode: 'AZ',
    title: 'HIPAA Compliance',
    description: 'Protected health information safeguards',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services - Office for Civil Rights',
    agencyAbbr: 'HHS-OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written HIPAA policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do all staff complete HIPAA training?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you have a designated Privacy Officer?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance program is complete.',
      atRisk: 'HIPAA program needs enhancement. Complete staff training and designate Privacy Officer.',
      nonCompliant: 'No HIPAA policies. This violates federal law.',
      unknown: 'Review HIPAA requirements.',
    },
    stateNotes: [
      'HIPAA Privacy Officer must be designated per 45 CFR 164.530',
      'Annual HIPAA training required for all staff',
      'Notice of Privacy Practices must be provided to patients',
      'Breach notification required within 60 days per 45 CFR 164.404',
    ],
  },

  {
    id: 'AZ-G3',
    itemId: 'G3',
    stateCode: 'AZ',
    title: 'Complaint and Grievance Procedures',
    description: 'Patient complaint handling documentation',
    severity: 'medium',
    regulationCode: 'R9-10-1208',
    regulationName: 'Complaints',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a formal complaint/grievance procedure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are patients informed of complaint procedures and ADHS hotline?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Are complaints documented, investigated, and resolved?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Complaint procedures documented and patients are informed.',
      atRisk: 'Ensure patients know how to file complaints and all complaints are documented.',
      nonCompliant: 'No complaint procedures documented.',
      unknown: 'Review complaint procedure requirements.',
    },
    stateNotes: [
      'ADHS Complaint Hotline: (602) 364-2536',
      'Patients must be informed of right to file complaints with ADHS',
    ],
  },

  {
    id: 'AZ-G4',
    itemId: 'G4',
    stateCode: 'AZ',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: 'R9-10-1206',
    regulationName: 'Record Maintenance',
    agencyName: 'Arizona Department of Health Services',
    agencyAbbr: 'ADHS',
    agencyPhone: '(602) 364-2536',
    agencyUrl: 'https://www.azdhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'hha_license', label: 'HHA license (renews every 2 years)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'fcc', label: 'Fingerprint Clearance Cards (renews every 6 years)' },
          { value: 'tb', label: 'TB screenings' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'training', label: 'Annual training due dates' },
          { value: 'nursing', label: 'RN/LPN licenses' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Implement tracking for all required documents.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'HHA license renews every 2 years - track 120 days in advance',
      'FCCs renew every 6 years - track 90 days in advance',
      'CPR certifications typically expire every 2 years',
      'Set automated reminders for all critical expirations',
    ],
  },

  {
    id: 'AZ-G5',
    itemId: 'G5',
    stateCode: 'AZ',
    title: 'Billing Documentation Accuracy',
    description: 'Clinical documentation supports billed services',
    severity: 'high',
    regulationCode: '42 CFR 424',
    regulationName: 'Billing',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do clinical notes support all billed services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, documentation always supports billing' },
          { value: 'usually', label: 'Usually' },
          { value: 'inconsistent', label: 'Inconsistent' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you conduct periodic billing audits?',
        inputType: 'select',
        options: [
          { value: 'quarterly', label: 'Yes, at least quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No audits' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'inconsistent', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Billing documentation is accurate and audited regularly.',
      atRisk: 'Improve documentation consistency and implement regular audits.',
      nonCompliant: 'Documentation does not support billing. This creates fraud risk.',
      unknown: 'Review billing documentation requirements.',
    },
    stateNotes: [
      'Clinical documentation must support all billed services per 42 CFR 424',
      'Conduct quarterly billing audits to identify issues',
      'Medicare/AHCCCS audits can result in recoupments and penalties',
      'Maintain documentation for 7 years for audit purposes',
    ],
  },
];

export default ARIZONA_QUESTIONS;